import Swal from 'sweetalert2';
import { capitalSpinnerHtml } from '@/lib/capitalSpinner';

export type { SweetAlertIcon } from 'sweetalert2';

type AlertOptions = {
    text?: string;
    html?: string;
    timer?: number;
};

type ApiErrorShape = {
    response?: {
        status?: number;
        data?: {
            message?: string;
            errors?: Record<string, string[]>;
        };
    };
};

export const SERVER_ERROR_MESSAGE = 'Ocorreu um erro no servidor. Tente novamente em instantes; se continuar, avise a TI.';

export const extractErrorMessage = (error: unknown, fallback = 'Tente novamente.'): string => {
    const response = (error as ApiErrorShape)?.response;
    // 5xx traz texto técnico (em dev, a exceção crua: "Pusher error: ..."). O
    // usuário não age sobre ele — o detalhe vai pro log/Sentry do backend.
    if (response?.status !== undefined && response.status >= 500) return SERVER_ERROR_MESSAGE;

    const data = response?.data;
    const primeiroCampo = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined;

    return primeiroCampo ?? data?.message ?? fallback;
};

/** Espera de ação do usuário com o spinner único da Capital (o mesmo de `components/CapitalSpinner`). */
export const showLoadingAlert = (title: string, text?: string) => {
    Swal.fire({
        title,
        html: `<div class="cc-spinner-alerta">${capitalSpinnerHtml(56)}</div>${TEXTO(text)}`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
    });
};

export const closeAlert = () => Swal.close();

export interface AlertaProgresso {
    /** Completa a barra, troca pelo check e fecha sozinho 1,5 s depois. Resolve quando o alerta fecha. */
    concluir: (title: string, text?: string) => Promise<void>;
}

const escapaHtml = (t: string) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

const TEXTO = (text?: string) => (text ? `<p class="cc-progresso__texto">${escapaHtml(text)}</p>` : '');

/** Tempo da barra fechando em 100% e do desenho do check — ver `sweetalert.css`. */
const BARRA_FECHA_MS = 380;
const CHECK_DESENHA_MS = 650;
const CHECK_VISIVEL_MS = 1500;

/**
 * Espera com fim visível: a barra avança sozinha (desacelerando, sem chegar ao
 * fim) enquanto a requisição roda; `concluir` completa a barra, desenha o check
 * e fecha 1,5 s depois. Serve quando o usuário precisa VER que terminou — um
 * download que começa sem aviso passava por "o alerta sumiu, deu certo?".
 * Falhou? `showErrorAlert` substitui este alerta, como no loading comum.
 */
export const showProgressAlert = (title: string, text?: string): AlertaProgresso => {
    void Swal.fire({
        title,
        html: `<div class="cc-progresso"><div class="cc-progresso__trilho"><div class="cc-progresso__barra"></div></div>${TEXTO(text)}</div>`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
    });

    return {
        concluir: (tituloFim, textoFim) =>
            new Promise<void>((resolve) => {
                const barra = Swal.getPopup()?.querySelector<HTMLElement>('.cc-progresso__barra');
                if (!Swal.isVisible() || !barra) {
                    void showSuccessAlert(tituloFim, { text: textoFim, timer: CHECK_VISIVEL_MS }).then(() => resolve());
                    return;
                }
                // Congela a barra onde a animação parou e completa a partir dali.
                barra.style.width = getComputedStyle(barra).width;
                barra.style.animation = 'none';
                void barra.offsetWidth;
                barra.classList.add('cc-progresso__barra--fim');

                window.setTimeout(() => {
                    const html = Swal.getHtmlContainer();
                    if (html) {
                        html.innerHTML = `<div class="cc-progresso cc-progresso--ok"><svg class="cc-check" viewBox="0 0 52 52" aria-hidden="true"><circle class="cc-check__circulo" cx="26" cy="26" r="24" fill="none"/><path class="cc-check__traco" fill="none" d="M15 27l7 7 15-15"/></svg>${TEXTO(textoFim)}</div>`;
                    }
                    const titulo = Swal.getTitle();
                    if (titulo) titulo.textContent = tituloFim;
                    window.setTimeout(() => {
                        Swal.close();
                        resolve();
                    }, CHECK_DESENHA_MS + CHECK_VISIVEL_MS);
                }, BARRA_FECHA_MS);
            }),
    };
};

export const showSuccessAlert = (title: string, options: AlertOptions = {}) => {
    const { text, html, timer = 1600 } = options;

    return Swal.fire({
        icon: 'success',
        title,
        text,
        html,
        timer,
        showConfirmButton: false,
    });
};

/** Informação neutra (limite atingido, nada a fazer): não é erro nem sucesso. */
export const showInfoAlert = (title: string, options: AlertOptions = {}) => {
    const { text, html, timer } = options;

    return Swal.fire({
        icon: 'info',
        title,
        text,
        html,
        timer,
        showConfirmButton: timer === undefined,
        confirmButtonText: 'Entendi',
    });
};

/** Validação de formulário / pré-condição do usuário: não é erro do sistema. */
export const showWarningAlert = (title: string, options: AlertOptions = {}) => {
    const { text, html, timer } = options;

    return Swal.fire({
        icon: 'warning',
        title,
        text,
        html,
        timer,
        showConfirmButton: timer === undefined,
        confirmButtonText: 'Entendi',
    });
};

type ConfirmOptions = {
    text?: string;
    /** Corpo com marcação — só HTML montado pelo próprio código, nunca texto do usuário. */
    html?: string;
    /** Texto do botão de confirmar — o verbo da ação ("Revogar", "Encerrar gestão"), nunca "OK". */
    confirmText?: string;
    cancelText?: string;
    /** `danger` = ação destrutiva/irreversível (botão vermelho, ícone de alerta). */
    variant?: 'default' | 'danger';
    /** Ícone quando não é pergunta: aviso com ação ("Ver pendentes") usa `info`. */
    icon?: 'question' | 'info' | 'warning';
    /** Fecha sozinho (ms) como "não" — aviso que não pode prender a tela. */
    timer?: number;
    /** `false` = clique fora e Esc não fecham (aviso que precisa ser lido). */
    dismissible?: boolean;
};

/**
 * Pergunta antes de uma ação e resolve `true` se o usuário confirmou.
 *
 *   if (!(await showConfirmAlert('Revogar esta permissão?', { confirmText: 'Revogar', variant: 'danger' }))) return;
 */
export const showConfirmAlert = async (title: string, options: ConfirmOptions = {}): Promise<boolean> => {
    const { text, html, confirmText = 'Confirmar', cancelText = 'Cancelar', variant = 'default', icon, timer, dismissible = true } = options;

    const { isConfirmed } = await Swal.fire({
        icon: icon ?? (variant === 'danger' ? 'warning' : 'question'),
        timer,
        timerProgressBar: timer !== undefined,
        allowOutsideClick: dismissible,
        allowEscapeKey: dismissible,
        title,
        text,
        html,
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        ...(variant === 'danger' ? { confirmButtonColor: '#e7515a' } : {}),
    });

    return isConfirmed;
};

export const showErrorAlert = (title: string, options: AlertOptions = {}) => {
    const { text, html, timer } = options;

    return Swal.fire({
        icon: 'error',
        title,
        text,
        html,
        timer,
        showConfirmButton: timer === undefined,
        confirmButtonText: 'Entendi',
    });
};

type PromptOptions = {
    text?: string;
    /** Rótulo acima do campo. */
    label?: string;
    placeholder?: string;
    /** Valor inicial do campo. */
    value?: string;
    /** `textarea` pra motivo/justificativa; `text` pra nome curto. */
    input?: 'text' | 'textarea';
    maxLength?: number;
    /** Mensagem quando vazio; `false` aceita vazio. Padrão: "Preencha o campo." */
    required?: string | false;
    /** Validação extra: devolva a mensagem de erro ou `undefined`. */
    validate?: (value: string) => string | undefined;
    confirmText?: string;
    cancelText?: string;
    variant?: 'default' | 'danger';
};

/**
 * Pede um texto (motivo, justificativa, nome) e resolve com ele já sem espaços nas
 * pontas — ou `null` se o usuário cancelou.
 *
 *   const motivo = await showPromptAlert('Reprovar hora extra', { label: 'Motivo da recusa', confirmText: 'Reprovar', variant: 'danger' });
 *   if (motivo === null) return;
 */
export const showPromptAlert = async (title: string, options: PromptOptions = {}): Promise<string | null> => {
    const {
        text,
        label,
        placeholder,
        value = '',
        input = 'textarea',
        maxLength,
        required = 'Preencha o campo.',
        validate,
        confirmText = 'Confirmar',
        cancelText = 'Cancelar',
        variant = 'default',
    } = options;

    const r = await Swal.fire<string>({
        icon: variant === 'danger' ? 'warning' : 'question',
        title,
        text,
        input,
        inputLabel: label,
        inputPlaceholder: placeholder,
        inputValue: value,
        inputAttributes: maxLength ? { maxlength: String(maxLength) } : undefined,
        inputValidator: (v) => {
            const t = (v ?? '').trim();
            if (!t && required !== false) return required;
            return validate?.(t) ?? null;
        },
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        ...(variant === 'danger' ? { confirmButtonColor: '#e7515a' } : {}),
    });

    return r.isConfirmed ? String(r.value ?? '').trim() : null;
};

/**
 * Formulário curto montado em HTML (select + textarea) com leitura no `preConfirm`.
 * Resolve com o que o `preConfirm` devolveu, ou `null` se cancelou. Formulário maior = modal React.
 */
export const showFormAlert = async <T>(
    title: string,
    options: { html: string; preConfirm: () => T; confirmText?: string; cancelText?: string; icon?: 'question' | 'info' | 'warning' },
): Promise<T | null> => {
    const { html, preConfirm, confirmText = 'Confirmar', cancelText = 'Cancelar', icon = 'question' } = options;

    const r = await Swal.fire<T>({
        icon,
        title,
        html,
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        focusConfirm: false,
        preConfirm,
    });

    return r.isConfirmed && r.value !== undefined ? (r.value as T) : null;
};

/**
 * Único toast do app: notificação em tempo real (push), que não pode bloquear a tela.
 * Feedback de ação do usuário NUNCA é toast — use os alertas centrais acima.
 */
export const showNotificationToast = (title: string, options: { text?: string; icon?: 'success' | 'error' | 'warning' | 'info' | 'question' } = {}) =>
    Swal.fire({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        icon: options.icon ?? 'info',
        title,
        text: options.text,
    });
